import React, { useState, useEffect, useCallback } from 'react';
import { formatearMoneda, limpiarMoneda, valorCrudo } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';
import { esMexicana } from '../utils/calculoRfcCurp';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';

const stBase = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #db0011', paddingBottom: 8 },
  subtitle: { fontSize: 15, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  inputNum: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  readOnly: { backgroundColor: '#f3f4f6', fontWeight: 'bold' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
  saveBtn: { backgroundColor: '#1a3a6e', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  medLabel: { fontSize: 13, color: '#374151' },
  medSiNo: { display: 'flex', gap: 8, alignItems: 'center' },
};

const HSBC_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero', 'Casado': 'Casado',
  // Divorciado, Viudo, Union Libre y Sociedad de Convivencia no existen en el
  // catálogo de HSBC (solo Soltero/Casado/Otro) — se mapean a "Otro" y se
  // escribe la etiqueta original en el campo "Especificar Estado Civil".
  'Divorciado': 'Otro', 'Viudo': 'Otro', 'Union Libre': 'Otro', 'Sociedad de Convivencia': 'Otro',
};
const HSBC_REGIMEN_MAP = {
  'Sociedad Conyugal': 'Bienes Mancomunados',
  'Separación de Bienes': 'Bienes Separados',
};
function idPorNombreHSBC(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const SiNo = ({ value, onChange, defaultNo }) => {
  useEffect(() => {
    if (defaultNo && (value === undefined || value === null)) onChange(false);
  }, [value, defaultNo, onChange]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (value === undefined || value === null) && defaultNo ? false : value;
  return (
    <div style={stBase.medSiNo}>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === true} onChange={() => onChange(true)} /> Sí
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
};

const BancoHSBC = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const st = {
    ...stBase,
    grid2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 },
    grid3: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 16, marginBottom: 16 },
    grid4: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
    medRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 120px', gap: 12, alignItems: isMobile ? 'start' : 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' },
  };

  const [nacionalidades,    setNacionalidades]    = useState([]);
  const [estadosCiviles,    setEstadosCiviles]    = useState([]);
  const [regimenes,         setRegimenes]         = useState([]);
  const [escolaridades,     setEscolaridades]     = useState([]);
  const [tiposContrato,     setTiposContrato]     = useState([]);
  const [puestos,           setPuestos]           = useState([]);
  const [actividades,       setActividades]       = useState([]);
  const [plazos,            setPlazos]            = useState([]);
  const [usosInmueble,      setUsosInmueble]      = useState([]);
  const [destinos,          setDestinos]          = useState([]);
  const [subproductos,      setSubproductos]      = useState([]);

  const [hs, setHs] = useState({});
  const [focusedMoneyField, setFocusedMoneyField] = useState(null);
  const [hsbcCargado, setHsbcCargado] = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  useEffect(() => {
    if (!prospectoId) { setHsbcCargado(true); return; }
    fetch(`${API}/hsbc/${prospectoId}`)
      .then(r => r.ok ? r.json() : null)
      .then(datos => {
        if (datos && datos.id) {
          const monto = parseFloat(datos.cred_monto) || 0;
          const valorInmueble = parseFloat(datos.cred_valor_inmueble) || 0;
          datos = {
            ...datos,
            titular_total_ingresos: sumaIngresos(datos, 'titular'),
            coac_total_ingresos: sumaIngresos(datos, 'coac'),
          };
          if (valorInmueble > 0) {
            datos = { ...datos, cred_porcentaje_financiamiento: Math.round((monto / valorInmueble) * 100) };
          }
          setHs(datos);
          setFormData(prev => ({ ...prev, hsbc: datos }));
        }
      })
      .catch(e => console.error('Error cargando datos HSBC:', e))
      .finally(() => setHsbcCargado(true));
  }, [prospectoId]);

  // Estado Civil, Régimen Matrimonial e Identificación (Titular y Coacreditado) se
  // derivan del módulo Titular / Coacreditado. Se recalculan cuando cambian ahí, y
  // también una vez que terminó de cargar lo guardado de HSBC, para no quedar
  // pisados por un valor anterior guardado en la base de datos.
  useEffect(() => {
    if (!hsbcCargado) return;
    if (estadosCiviles.length === 0 || regimenes.length === 0 || nacionalidades.length === 0) return;

    const derivar = (persona) => {
      const ecCentral = persona?.estado_civil;
      const ecNombre = HSBC_ESTADO_CIVIL_MAP[ecCentral] || null;
      const regNombre = ecCentral === 'Casado' ? (HSBC_REGIMEN_MAP[persona?.regimen_matrimonio] || null) : null;
      const mexicana = esMexicana(persona?.nacionalidad);
      return {
        ecId: idPorNombreHSBC(estadosCiviles, ecNombre),
        ecOtro: ecNombre === 'Otro' ? ecCentral : '',
        regId: idPorNombreHSBC(regimenes, regNombre),
        tipoId: persona?.tipo_identificacion || '',
        numId: persona?.numero_identificacion || '',
        nacId: idPorNombreHSBC(nacionalidades, mexicana ? 'Mexicana' : 'Extranjera'),
        nacEspecifica: mexicana ? '' : (persona?.nacionalidad || ''),
        paisResidencia: persona?.pais_residencia || '',
      };
    };
    const tit = derivar(formData.titular);
    const coac = derivar(formData.coacreditado);

    setHs(prev => {
      const next = { ...prev };
      let changed = false;
      const set = (k, v) => { if (next[k] !== v) { next[k] = v; changed = true; } };
      set('titular_estado_civil_id', tit.ecId);
      set('titular_estado_civil_otro', tit.ecOtro);
      set('titular_regimen_matrimonial_id', tit.regId);
      set('titular_tipo_identificacion', tit.tipoId);
      set('titular_num_identificacion', tit.numId);
      set('titular_nacionalidad_id', tit.nacId);
      set('titular_nacionalidad_especifica', tit.nacEspecifica);
      set('titular_pais_residencia', tit.paisResidencia);
      set('coac_estado_civil_id', coac.ecId);
      set('coac_estado_civil_otro', coac.ecOtro);
      set('coac_regimen_matrimonial_id', coac.regId);
      set('coac_tipo_identificacion', coac.tipoId);
      set('coac_num_identificacion', coac.numId);
      set('coac_nacionalidad_id', coac.nacId);
      set('coac_nacionalidad_especifica', coac.nacEspecifica);
      set('coac_pais_residencia', coac.paisResidencia);
      return changed ? next : prev;
    });
  }, [
    hsbcCargado, estadosCiviles, regimenes, nacionalidades,
    formData.titular.estado_civil, formData.titular.regimen_matrimonio,
    formData.titular.tipo_identificacion, formData.titular.numero_identificacion,
    formData.titular.nacionalidad, formData.titular.pais_residencia,
    formData.coacreditado?.estado_civil, formData.coacreditado?.regimen_matrimonio,
    formData.coacreditado?.tipo_identificacion, formData.coacreditado?.numero_identificacion,
    formData.coacreditado?.nacionalidad, formData.coacreditado?.pais_residencia,
  ]);

  const cargarCatalogos = async () => {
    try {
      const cats = await Promise.all([
        fetch(`${API}/catalogos/cat_hsbc_nacionalidad`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_estado_civil`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_regimen_matrimonial`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_escolaridad`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_tipo_contrato`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_puesto_ocupacion`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_actividad_giro`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_plazo_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_uso_inmueble`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_destino`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_hsbc_subproducto`).then(r => r.json()),
      ]);
      setNacionalidades(cats[0]); setEstadosCiviles(cats[1]); setRegimenes(cats[2]);
      setEscolaridades(cats[3]);  setTiposContrato(cats[4]);  setPuestos(cats[5]);
      setActividades(cats[6]);    setPlazos(cats[7]);         setUsosInmueble(cats[8]);
      setDestinos(cats[9]);       setSubproductos(cats[10]);
    } catch (e) {
      console.error('Error cargando catálogos HSBC:', e);
    }
  };

  const sumaIngresos = (datos, prefix) =>
    ((parseFloat(datos[`${prefix}_ingreso_bruto`]) || 0)
    + (parseFloat(datos[`${prefix}_otros_ingresos`]) || 0)).toFixed(2);

  const marcarOpcion = useCallback((opciones, campoElegido) => {
    setHs(prev => {
      const next = { ...prev };
      opciones.forEach(o => {
        next[o.campo] = (o.campo === campoElegido);
        (o.hijos || []).forEach(h => { next[h] = null; });
      });
      return next;
    });
  }, []);

  const upd = useCallback((campo, valor) => {
    setHs(prev => {
      const next = { ...prev, [campo]: valor };
      if (['titular_ingreso_bruto', 'titular_otros_ingresos'].includes(campo)) {
        next.titular_total_ingresos = sumaIngresos(next, 'titular');
      }
      if (['coac_ingreso_bruto', 'coac_otros_ingresos'].includes(campo)) {
        next.coac_total_ingresos = sumaIngresos(next, 'coac');
      }
      if (['cred_monto', 'cred_valor_inmueble'].includes(campo)) {
        const monto = parseFloat(campo === 'cred_monto' ? valor : next.cred_monto) || 0;
        const valorInmueble = parseFloat(campo === 'cred_valor_inmueble' ? valor : next.cred_valor_inmueble) || 0;
        next.cred_porcentaje_financiamiento = valorInmueble > 0 ? Math.round((monto / valorInmueble) * 100) : '';
      }
      return next;
    });
  }, []);

  const guardar = async () => {
    if (!prospectoId) {
      alert('Primero guarde el prospecto antes de agregar información del banco');
      return;
    }
    if (creditoExcedeInmueble(hs.cred_monto, hs.cred_valor_inmueble)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    try {
      const r = await fetch(`${API}/hsbc/${prospectoId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospecto_id: prospectoId, ...hs }),
      });
      const data = await r.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, hsbc: hs }));
        alert('✓ Información de HSBC guardada correctamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (e) {
      alert('Error al guardar la información');
    }
  };

  const tieneCoac = !!formData.coacreditado?.nombres;
  const coacParticipaIngresos = formData.coacreditado?.participa_ingresos === true;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const renderSiNo = (campo) => (
    <select
      value={hs[campo] === true ? 'true' : hs[campo] === false ? 'false' : ''}
      onChange={e => upd(campo, e.target.value === '' ? null : e.target.value === 'true')}
      style={st.input}
    >
      <option value="">-- Seleccionar --</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>
  );

  const renderBifurcacion = (pregunta, opciones) => (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>{pregunta}</p>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {opciones.map(o => (
          <label key={o.campo} style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={hs[o.campo] === true} onChange={() => marcarOpcion(opciones, o.campo)} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );

  const rama = { marginLeft: 20, borderLeft: '2px solid #e5e7eb', paddingLeft: 16, marginTop: 4, marginBottom: 4 };

  const renderSelect = (campo, opciones) => (
    <select value={hs[campo] || ''} onChange={e => upd(campo, e.target.value)} style={st.input}>
      <option value="">-- Seleccionar --</option>
      {opciones.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
    </select>
  );

  const renderMoney = (label, campo, decimales = 0) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <div style={st.moneyWrap}>
        <span style={st.moneySign}>$</span>
        <input type="text" inputMode="decimal"
          value={focusedMoneyField === campo ? valorCrudo(hs[campo], decimales) : formatearMoneda(hs[campo], decimales)}
          onFocus={() => setFocusedMoneyField(campo)}
          onBlur={() => setFocusedMoneyField(null)}
          onChange={e => upd(campo, limpiarMoneda(e.target.value, decimales))}
          style={{ ...st.inputNum, paddingLeft: 24 }} placeholder={decimales === 0 ? '0' : '0.00'} />
      </div>
    </div>
  );

  const renderMoneyRO = (label, campo, decimales = 0) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <input readOnly
        value={`$${formatearMoneda(hs[campo] || 0, decimales)}`}
        style={{ ...st.input, ...st.readOnly }} />
    </div>
  );

  // ── Sección Info General ─────────────────────────────────────────────────────
  const renderInfoGeneral = (prefix, titulo) => {
    const esExtranjero = parseInt(hs[`${prefix}_nacionalidad_id`]) === 2;
    const esCasado     = parseInt(hs[`${prefix}_estado_civil_id`]) === 2;
    const esOtroEC     = parseInt(hs[`${prefix}_estado_civil_id`]) === 3;
    return (
      <div style={st.card}>
        <h3 style={st.title}>{titulo}</h3>

        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>País de Residencia</label>
            <input readOnly value={hs[`${prefix}_pais_residencia`] || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Tipo de Identificación</label>
            <input readOnly value={hs[`${prefix}_tipo_identificacion`] || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Número de Identificación</label>
            <input readOnly value={hs[`${prefix}_num_identificacion`] || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Nacionalidad</label>
            <input readOnly value={nacionalidades.find(n => n.id === hs[`${prefix}_nacionalidad_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
        </div>

        {esExtranjero && (
          <div style={st.grid2}>
            <div style={st.field}>
              <label style={st.label}>Especificar Nacionalidad (Extranjero)</label>
              <input readOnly value={hs[`${prefix}_nacionalidad_especifica`] || ''}
                style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
            </div>
          </div>
        )}

        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Estado Civil</label>
            <input readOnly value={estadosCiviles.find(ec => ec.id === hs[`${prefix}_estado_civil_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          {esOtroEC && (
            <div style={st.field}>
              <label style={st.label}>Especificar Estado Civil</label>
              <input readOnly value={hs[`${prefix}_estado_civil_otro`] || ''}
                style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
            </div>
          )}
          {esCasado && (
            <div style={st.field}>
              <label style={st.label}>Régimen Matrimonial</label>
              <input readOnly value={regimenes.find(r => r.id === hs[`${prefix}_regimen_matrimonial_id`])?.nombre || ''}
                style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
            </div>
          )}
          <div style={st.field}>
            <label style={st.label}>Escolaridad</label>
            {renderSelect(`${prefix}_escolaridad_id`, escolaridades)}
          </div>
          {prefix === 'coac' && (
            <div style={st.field}>
              <label style={st.label}>Parentesco con el Titular</label>
              <input value={hs.coac_parentesco || ''}
                onChange={e => upd('coac_parentesco', e.target.value)} style={st.input} placeholder="Cónyuge, Hijo, Padre..." />
            </div>
          )}
        </div>

        {esExtranjero && (
          <>
            <p style={st.subtitle}>Dirección en País de Origen</p>
            <div style={st.grid4}>
              <div style={st.field}>
                <label style={st.label}>Calle</label>
                <input value={hs[`${prefix}_ext_calle`] || ''}
                  onChange={e => upd(`${prefix}_ext_calle`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>No. Exterior</label>
                <input value={hs[`${prefix}_ext_no_ext`] || ''}
                  onChange={e => upd(`${prefix}_ext_no_ext`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>No. Interior</label>
                <input value={hs[`${prefix}_ext_no_int`] || ''}
                  onChange={e => upd(`${prefix}_ext_no_int`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>C.P.</label>
                <input value={hs[`${prefix}_ext_cp`] || ''}
                  onChange={e => upd(`${prefix}_ext_cp`, e.target.value)} style={st.input} />
              </div>
            </div>
            <div style={st.grid4}>
              <div style={st.field}>
                <label style={st.label}>Colonia</label>
                <input value={hs[`${prefix}_ext_colonia`] || ''}
                  onChange={e => upd(`${prefix}_ext_colonia`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Delegación / Municipio</label>
                <input value={hs[`${prefix}_ext_municipio`] || ''}
                  onChange={e => upd(`${prefix}_ext_municipio`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Ciudad</label>
                <input value={hs[`${prefix}_ext_ciudad`] || ''}
                  onChange={e => upd(`${prefix}_ext_ciudad`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Entidad Federativa</label>
                <input value={hs[`${prefix}_ext_entidad`] || ''}
                  onChange={e => upd(`${prefix}_ext_entidad`, e.target.value)} style={st.input} />
              </div>
            </div>
            <div style={st.grid2}>
              <div style={st.field}>
                <label style={st.label}>País</label>
                <input value={hs[`${prefix}_ext_pais`] || ''}
                  onChange={e => upd(`${prefix}_ext_pais`, e.target.value)} style={st.input} />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Sección Trabajo ──────────────────────────────────────────────────────────
  const renderTrabajo = (prefix, titulo) => (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>
      <div style={st.grid4}>
        <div style={st.field}>
          <label style={st.label}>Tipo de Contrato</label>
          {renderSelect(`${prefix}_tipo_contrato_id`, tiposContrato)}
        </div>
        <div style={st.field}>
          <label style={st.label}>Puesto / Ocupación</label>
          {renderSelect(`${prefix}_puesto_id`, puestos)}
        </div>
        <div style={st.field}>
          <label style={st.label}>Actividad / Giro</label>
          {renderSelect(`${prefix}_actividad_giro_id`, actividades)}
        </div>
        <div style={st.field}>
          <label style={st.label}>Actividad Específica</label>
          <input value={hs[`${prefix}_actividad_especifica`] || ''}
            onChange={e => upd(`${prefix}_actividad_especifica`, e.target.value)} style={st.input} />
        </div>
      </div>
      <div style={st.grid4}>
        {renderMoney('Ingreso Bruto Mensual Fijo', `${prefix}_ingreso_bruto`)}
        {renderMoney('Otros Ingresos', `${prefix}_otros_ingresos`)}
        <div style={st.field}>
          <label style={st.label}>Fuente de Otros Ingresos</label>
          <input value={hs[`${prefix}_fuente_otros_ingresos`] || ''}
            onChange={e => upd(`${prefix}_fuente_otros_ingresos`, e.target.value)} style={st.input} />
        </div>
        {renderMoneyRO('Total Ingresos', `${prefix}_total_ingresos`)}
      </div>
    </div>
  );

  // ── Cuestionario Médico ──────────────────────────────────────────────────────
  const preguntas = [
    { key: '1a', texto: '1a. Infarto, angina, problemas cardíacos, hipertensión/hipotensión' },
    { key: '1b', texto: '1b. Convulsiones, epilepsia, derrame cerebral, problemas del sistema nervioso' },
    { key: '1c', texto: '1c. Tumores malignos, cáncer, leucemia, melanoma, linfoma' },
    { key: '1d', texto: '1d. Enfermedad renal, trastorno hepático' },
    { key: '1e', texto: '1e. Asma, enfisema, tuberculosis, enfermedad pulmonar' },
    { key: '1f', texto: '1f. SIDA, VIH positivo, Lupus' },
    { key: '1g', texto: '1g. Diabetes, niveles elevados de azúcar, enfermedades tiroideas, problemas endocrinos' },
    { key: '1h', texto: '1h. Trastornos de columna, enfermedades de huesos/oídos/ojos/sangre' },
    { key: '2',  texto: '2. ¿Tiene programada intervención quirúrgica u hospitalización?' },
    { key: '3',  texto: '3. ¿Tiene enfermedad o limitación física que impida desempeñar su actividad?' },
    { key: '4',  texto: '4. ¿Es bombero, minero, obrero petrolero o está expuesto a riesgos?' },
    { key: '5',  texto: '5. ¿Practica deportes de alto riesgo?' },
  ];

  const PREGUNTAS_1A3 = ['1a', '1b', '1c', '1d', '1e', '1f', '1g', '1h', '2', '3'];

  const renderMedico = (prefix, titulo) => {
    const hayAlgunaSi = PREGUNTAS_1A3.some(k => hs[`med_${prefix}_${k}`] === true);
    return (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>
      {preguntas.map(({ key, texto }) => (
        <div key={key} style={st.medRow}>
          <span style={st.medLabel}>{texto}</span>
          <SiNo value={hs[`med_${prefix}_${key}`]} onChange={v => upd(`med_${prefix}_${key}`, v)} defaultNo />
        </div>
      ))}
      {hayAlgunaSi && (
        <>
          <p style={{ ...st.subtitle, marginTop: 20 }}>Detalles (si alguna respuesta fue Sí en preguntas 1-3)</p>
          {[1, 2].map(n => (
            <div key={n} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <p style={{ fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 8 }}>Registro {n}</p>
              <div style={st.grid4}>
                <div style={st.field}>
                  <label style={st.label}>No. Pregunta</label>
                  <input type="number" max="3" value={hs[`med_${prefix}_det${n}_pregunta`] || ''}
                    onChange={e => upd(`med_${prefix}_det${n}_pregunta`, e.target.value)} style={st.inputNum} placeholder="1, 2 o 3" />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Fecha (DD/MM/AAAA)</label>
                  <input value={hs[`med_${prefix}_det${n}_fecha`] || ''}
                    onChange={e => upd(`med_${prefix}_det${n}_fecha`, e.target.value)} style={st.input} placeholder="01/01/2020" />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Duración</label>
                  <input value={hs[`med_${prefix}_det${n}_duracion`] || ''}
                    onChange={e => upd(`med_${prefix}_det${n}_duracion`, e.target.value)} style={st.input} placeholder="6 meses, crónica..." />
                </div>
              </div>
              <div style={st.grid2}>
                <div style={st.field}>
                  <label style={st.label}>Nombre de Enfermedades / Lesiones / Tratamientos</label>
                  <input value={hs[`med_${prefix}_det${n}_enfermedades`] || ''}
                    onChange={e => upd(`med_${prefix}_det${n}_enfermedades`, e.target.value)} style={st.input} />
                </div>
                <div style={st.field}>
                  <label style={st.label}>Condiciones Actuales</label>
                  <input value={hs[`med_${prefix}_det${n}_condiciones`] || ''}
                    onChange={e => upd(`med_${prefix}_det${n}_condiciones`, e.target.value)} style={st.input} />
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
    );
  };

  return (
    <div>
      {/* ── Titular ── */}
      {renderInfoGeneral('titular', '👤 Información General del Titular')}
      {renderTrabajo('titular', '💼 Información de Trabajo del Titular')}

      {/* ── Coacreditado ── */}
      {tieneCoac && renderInfoGeneral('coac', '👤 Información General del Coacreditado')}
      {tieneCoac && coacParticipaIngresos && renderTrabajo('coac', '💼 Información de Trabajo del Coacreditado')}

      {/* ── Cuestionario Médico ── */}
      {renderMedico('tit', '🏥 Cuestionario Médico del Titular')}
      {tieneCoac && renderMedico('coac', '🏥 Cuestionario Médico del Coacreditado')}

      {/* ── Información del Crédito ── */}
      <div style={st.card}>
        <h3 style={st.title}>Información del Crédito</h3>
        <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 8, marginBottom: 16 }}>
          <div style={{ width: isMobile ? '100%' : 157 }}>
            <div style={st.field}>
              <label style={st.label}>Oferta Vinculante</label>
              {renderSiNo('cred_oferta_vinculante')}
            </div>
          </div>
          <div style={{ width: isMobile ? '100%' : 314 }}>
            <div style={st.field}>
              <label style={st.label}>Destino</label>
              <select value={hs.cred_destino_id || ''} onChange={e => {
                const nuevoId = e.target.value;
                const nombreNuevo = destinos.find(d => String(d.id) === String(nuevoId))?.nombre;
                upd('cred_destino_id', nuevoId);
                if (nombreNuevo !== 'Adquisición de Vivienda') upd('cred_subproducto_id', '');
              }} style={st.input}>
                <option value="">-- Seleccionar --</option>
                {destinos.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
              </select>
            </div>
          </div>
          {destinos.find(d => String(d.id) === String(hs.cred_destino_id))?.nombre === 'Adquisición de Vivienda' && (
            <div style={{ width: isMobile ? '100%' : 188 }}>
              <div style={st.field}>
                <label style={st.label}>Subproducto</label>
                {renderSelect('cred_subproducto_id', subproductos)}
              </div>
            </div>
          )}
          <div style={{ width: isMobile ? '100%' : 157 }}>
            <div style={st.field}>
              <label style={st.label}>Tipo de Pago</label>
              <select value={hs.cred_pago_tipo || ''} onChange={e => upd('cred_pago_tipo', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                <option value="fijo">Pago Fijo</option>
                <option value="bajo">Pago Bajo</option>
              </select>
            </div>
          </div>
          <div style={{ width: isMobile ? '100%' : 157 }}>
            <div style={st.field}>
              <label style={st.label}>Plazo</label>
              {renderSelect('cred_plazo_id', plazos)}
            </div>
          </div>
        </div>
        <div style={st.grid4}>
          {renderMoney('Monto Crédito Solicitado', 'cred_monto')}
          <div style={st.field}>
            <label style={st.label}>Tasa Interés Fija Anual (%)</label>
            <input type="number" step="0.01" value={hs.cred_tasa_interes || ''}
              onChange={e => upd('cred_tasa_interes', e.target.value)} style={st.inputNum} placeholder="0.00" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Uso de Inmueble</label>
            {renderSelect('cred_uso_inmueble_id', usosInmueble)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Tipo de Inmueble</label>
            <select value={hs.cred_tipo_inmueble || ''} onChange={e => upd('cred_tipo_inmueble', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="nuevo">Nuevo</option>
              <option value="usado">Usado</option>
            </select>
          </div>
        </div>
        <div style={st.grid4}>
          {renderMoney('Valor Estimado del Inmueble', 'cred_valor_inmueble')}
          <div style={st.field}>
            <label style={st.label}>Porcentaje de Financiamiento (%)</label>
            <input readOnly value={hs.cred_porcentaje_financiamiento !== '' && hs.cred_porcentaje_financiamiento != null ? `${hs.cred_porcentaje_financiamiento}` : ''}
              style={{ ...st.inputNum, ...st.readOnly }} placeholder="0" />
          </div>
        </div>
      </div>

      {/* ── Carta Selección Productos ── */}
      <div style={st.card}>
        <h3 style={st.title}>📋 Carta Selección Productos</h3>

        {renderBifurcacion('¿Deseas adquirir una vivienda? (Nueva o usada)', [
          { campo: 'selec1', label: 'Sí', hijos: ['selec2', 'selec3', 'selec4', 'selec5', 'selec6', 'selec7', 'selec8', 'selec9', 'selec10', 'selec11'] },
          { campo: 'selec12', label: 'No', hijos: ['selec13', 'selec14', 'selec15', 'selec16', 'selec17', 'selec18', 'selec19', 'selec20', 'selec21', 'selec22'] },
        ])}

        {hs.selec1 === true && (
          <div style={rama}>
            {renderBifurcacion('¿Cuál es tu fuente de ingresos principal?', [
              { campo: 'selec2', label: 'Asalariado', hijos: ['selec4', 'selec5', 'selec6', 'selec7', 'selec8', 'selec9'] },
              { campo: 'selec3', label: 'Profesionista Independiente, PFAE o dueño de negocio', hijos: ['selec10', 'selec11'] },
            ])}

            {hs.selec2 === true && (
              <div style={rama}>
                {renderBifurcacion('Estás afiliado a:', [
                  { campo: 'selec4', label: 'Infonavit', hijos: ['selec6', 'selec7'] },
                  { campo: 'selec5', label: 'Ninguno', hijos: ['selec8', 'selec9'] },
                ])}

                {hs.selec4 === true && (
                  <div style={rama}>
                    {renderBifurcacion('¿Deseas obtener un crédito cofinanciado?', [
                      { campo: 'selec6', label: 'Sí', hijos: [] },
                      { campo: 'selec7', label: 'No', hijos: [] },
                    ])}
                  </div>
                )}
                {hs.selec5 === true && (
                  <div style={rama}>
                    {renderBifurcacion('¿Deseas obtener un crédito cofinanciado?', [
                      { campo: 'selec8', label: 'Sí', hijos: [] },
                      { campo: 'selec9', label: 'No', hijos: [] },
                    ])}
                  </div>
                )}
              </div>
            )}

            {hs.selec3 === true && (
              <div style={rama}>
                {renderBifurcacion('¿Eres derechohabiente no activo del Infonavit, y deseas utilizar el saldo generado en la Subcuenta de Vivienda?', [
                  { campo: 'selec10', label: 'Sí', hijos: [] },
                  { campo: 'selec11', label: 'No', hijos: [] },
                ])}
              </div>
            )}
          </div>
        )}

        {hs.selec12 === true && (
          <div style={rama}>
            {renderBifurcacion('¿Tienes un crédito hipotecario con otra institución financiera que desees mejorar sus condiciones?', [
              { campo: 'selec13', label: 'Sí', hijos: ['selec15', 'selec16'] },
              { campo: 'selec14', label: 'No', hijos: ['selec17', 'selec18', 'selec19', 'selec20', 'selec21', 'selec22'] },
            ])}

            {hs.selec13 === true && (
              <div style={rama}>
                {renderBifurcacion('¿Deseas obtener recursos financieros adicionales dejando como garantía el inmueble hipotecado?', [
                  { campo: 'selec15', label: 'Sí', hijos: [] },
                  { campo: 'selec16', label: 'No', hijos: [] },
                ])}
              </div>
            )}

            {hs.selec14 === true && (
              <div style={rama}>
                {renderBifurcacion('¿Deseas obtener recursos financieros con destino libre, para consolidar deudas?', [
                  { campo: 'selec17', label: 'Sí', hijos: ['selec19', 'selec20'] },
                  { campo: 'selec18', label: 'No', hijos: ['selec21', 'selec22'] },
                ])}

                {hs.selec17 === true && (
                  <div style={rama}>
                    {renderBifurcacion('¿Cuentas con un inmueble libre de gravamen del cuál seas propietario y deseas establecerlo como garantía de un préstamo?', [
                      { campo: 'selec19', label: 'Sí', hijos: [] },
                      { campo: 'selec20', label: 'No', hijos: [] },
                    ])}
                  </div>
                )}
                {hs.selec18 === true && (
                  <div style={rama}>
                    {renderBifurcacion('¿Cuentas con un inmueble libre de gravamen del cual seas propietario y deseas obtener recursos financieros para construir o remodelar, dejándolo como garantía hipotecaria?', [
                      { campo: 'selec21', label: 'Sí', hijos: [] },
                      { campo: 'selec22', label: 'No', hijos: [] },
                    ])}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ ...st.field, marginTop: 16, maxWidth: isMobile ? '100%' : 400 }}>
          <label style={st.label}>Tipo de Crédito (Carta Selección)</label>
          <select value={hs.tipo_credito_hsbc || ''} onChange={e => upd('tipo_credito_hsbc', e.target.value)} style={st.input}>
            <option value="">-- Seleccionar --</option>
            <option value="Adquiere tu hogar">Adquiere tu hogar</option>
            <option value="Construye">Construye</option>
            <option value="Remodela">Remodela</option>
            <option value="Liquidez">Liquidez</option>
            <option value="Pago de Hipoteca">Pago de Hipoteca</option>
            <option value="Pago de Hipoteca más Liquidez">Pago de Hipoteca más Liquidez</option>
            <option value="Cuenta Infonavit + Crédito Bancario">Cuenta Infonavit + Crédito Bancario</option>
          </select>
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <button onClick={guardar} style={st.saveBtn}>
          💾 Guardar Información HSBC
        </button>
      </div>
    </div>
  );
};

export default BancoHSBC;
